<template>
	<div class="awsMaintenanceRoot">
		<div class="pageHeading">AWS Maintenance</div>

		<div v-if="error" class="errorBox">{{error}}</div>

		<div v-if="!permissionRead" class="errorBox">You do not have the Online Server Maintenance Read permission.</div>
		<template v-else>
			<div class="card toolbarCard">
				<button type="button" class="gradientButton lightBlue" @click="loadStatus" :disabled="loading">Refresh</button>
				<span v-if="loading" class="loadingInline"><ScaleLoader /> Querying AWS…</span>
				<span v-else-if="lastRefreshed" class="mutedText">Status as of {{lastRefreshed}}<template v-if="autoRefreshPaused"> — auto-refresh paused while idle</template></span>
				<span v-if="!permissionWrite" class="readOnlyBadge" title="You do not have the Online Server Maintenance Write permission.">read-only</span>
			</div>

			<template v-if="status">
				<div class="card">
					<div class="lbHeading"><code class="inline">{{status.displayName}}</code> Load Balancer</div>
					<div v-for="inst in status.instances" :key="inst.instanceId" class="instanceRow">
						<div class="instanceInfo">
							<span class="statusDot" :class="inst.ec2State === 'running' ? 'dotGreen' : 'dotGray'" :title="'EC2 instance state: ' + (inst.ec2State || 'unknown')"></span>
							<span class="instanceName">{{inst.name || inst.instanceId}}</span>
							<span class="mutedText">{{inst.instanceId}}</span>
							<span class="stateBadge" :class="rollup(inst).badgeClass" :title="registrationDetail(inst)">{{rollup(inst).label}}</span>
						</div>
						<div v-if="rollup(inst).detail" class="instanceDetail">{{rollup(inst).detail}}</div>
						<div v-if="permissionWrite" class="instanceActions">
							<span v-if="busyInstanceId === inst.instanceId" class="loadingInline"><ScaleLoader /> Working…</span>
							<template v-else>
								<slide-button v-if="rollup(inst).registerAction"
											  :key="'slide_reg_' + inst.instanceId + '_' + slideCounter"
											  :auto-width="false"
											  :circle="true"
											  :noanimate="true"
											  :width="240"
											  :height="50"
											  :text="rollup(inst).registerAction"
											  success-text="working"
											  :requireReleaseToComplete="true"
											  @completed="registerInstance(inst)" />
								<div v-if="rollup(inst).allowDeregister" class="slideRed" :title="canDeregister(inst) ? '' : 'Deregistration is not allowed because at least one healthy instance must remain registered with every target group.'">
									<slide-button :key="'slide_dereg_' + inst.instanceId + '_' + slideCounter"
												  :auto-width="false"
												  :circle="true"
												  :noanimate="true"
												  :width="240"
												  :height="50"
												  text="deregister"
												  success-text="working"
												  :disabled="!canDeregister(inst)"
												  :requireReleaseToComplete="true"
												  @completed="deregisterInstance(inst)" />
								</div>
							</template>
						</div>
					</div>
				</div>

				<div class="card">
					<div class="secondaryHeading">Deregistration Delay (drain time)</div>
					<p class="hintText">
						When an instance is deregistered, the load balancer stops sending it new requests and waits up to
						this many seconds ("draining") for in-flight requests to complete before the instance is fully removed.
					</p>
					<div class="formRow">
						<label class="formLabel" for="defaultDrainInput">Default drain time</label>
						<input id="defaultDrainInput" type="number" class="drainInput" min="0" max="3600" v-model.number="defaultDrainInput" :disabled="!permissionWrite" />
						<span>seconds</span>
					</div>
					<div class="drainReadout">
						<span v-for="tg in status.targetGroups" :key="tg.arn" class="drainTgValue" :class="{ drainWrong: tg.drainSeconds !== status.defaultDrainSeconds }" :title="tg.arn">
							{{tg.name}}: {{tg.drainSeconds >= 0 ? tg.drainSeconds + 's' : 'unknown'}}
						</span>
					</div>
					<div v-if="drainMismatch" class="warningBox">
						The deregistration delay configured at AWS does not match the assigned default of {{status.defaultDrainSeconds}} seconds.
						Slide below to commit the default to the target groups.
					</div>
					<div v-if="permissionWrite && showDefaultDrainCommit" class="drainCommitRow">
						<span v-if="drainCommitBusy" class="loadingInline"><ScaleLoader /> Applying…</span>
						<slide-button v-else
									  :key="'slide_drain_' + slideCounter"
									  :auto-width="false"
									  :circle="true"
									  :noanimate="true"
									  :width="300"
									  :height="50"
									  :text="'set drain to ' + defaultDrainInput + 's'"
									  success-text="working"
									  :disabled="!defaultDrainInputValid"
									  :requireReleaseToComplete="true"
									  @completed="commitDefaultDrain" />
					</div>

					<div class="actionDrainSection">
						<div class="formRow">
							<label class="formLabel" for="actionDrainInput">Drain time for deregistrations from this page</label>
							<input id="actionDrainInput" type="number" class="drainInput" min="0" max="3600" placeholder="default" v-model="actionDrainInput" :disabled="!permissionWrite" @change="saveActionDrain" />
							<span>seconds (leave blank to use the default)</span>
						</div>
						<p class="hintText">
							If set, deregistration actions performed on this page temporarily change the target groups' deregistration
							delay to this value, order the deregistration, and then restore the previous value.  Deregistrations
							performed elsewhere (e.g. by AWS maintenance automations) are unaffected and continue using the default.
						</p>
					</div>
				</div>
			</template>
			<div v-else-if="loading" class="card loadingRow"><ScaleLoader /> Loading status…</div>
		</template>
	</div>
</template>

<script>
	import SlideButton from 'appRoot/vues/common/controls/SlideButton.vue';
	import
		{
			GetAwsMaintenanceStatus, RegisterAwsMaintenanceInstance, DeregisterAwsMaintenanceInstance,
			SetAwsMaintenanceDefaultDrain, SetAwsMaintenanceActionDrain
		} from 'appRoot/api/AwsMaintenance';

	// Background refresh pauses after this long without user interaction, so an unattended page does not hammer the AWS API.
	const autoRefreshIdleMs = 60000;

	export default {
		components: { SlideButton },
		data()
		{
			return {
				error: null,
				loading: false,
				status: null,
				lastRefreshedAt: 0, // Timestamp (ms) of the last successful status query.
				now: Date.now(), // Updated on each refresh tick so [lastRefreshed] reacts to the passage of time.
				busyInstanceId: null,
				drainCommitBusy: false,
				slideCounter: 0, // Incremented to reset all slide buttons after any action or data change.
				defaultDrainInput: 0,
				actionDrainInput: '',
				refreshTimer: null,
				silentRefreshBusy: false,
				autoRefreshPaused: false,
			};
		},
		computed:
		{
			/// Formatted timestamp of the last successful status query, including the date once more than 12 hours old.
			lastRefreshed()
			{
				if (!this.lastRefreshedAt)
					return null;
				let d = new Date(this.lastRefreshedAt);
				return this.now - this.lastRefreshedAt > 12 * 60 * 60 * 1000 ? d.toLocaleString() : d.toLocaleTimeString();
			},
			permissionRead()
			{
				return this.$store.state.userData.OnlineServerMaintenanceRead || this.$store.state.userData.OnlineServerMaintenanceWrite;
			},
			permissionWrite()
			{
				return this.$store.state.userData.OnlineServerMaintenanceWrite;
			},
			drainMismatch()
			{
				if (!this.status)
					return false;
				return this.status.targetGroups.some(tg => tg.drainSeconds !== this.status.defaultDrainSeconds);
			},
			defaultDrainInputValid()
			{
				return typeof this.defaultDrainInput === 'number' && this.defaultDrainInput >= 0 && this.defaultDrainInput <= 3600;
			},
			showDefaultDrainCommit()
			{
				return this.drainMismatch || (this.defaultDrainInputValid && this.defaultDrainInput !== this.status.defaultDrainSeconds);
			}
		},
		created()
		{
			if (this.permissionRead)
			{
				this.loadStatus();
				// Draining and health-check states change over time, so refresh silently in the background.
				this.refreshTimer = setInterval(this.silentRefresh, 10000);
				// _lastInteraction is deliberately a non-reactive field because mouse movement updates it constantly.
				this._lastInteraction = Date.now();
				this._interactionEvents = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];
				for (let i = 0; i < this._interactionEvents.length; i++)
					document.addEventListener(this._interactionEvents[i], this.onUserInteraction, { passive: true, capture: true });
			}
		},
		beforeUnmount()
		{
			clearInterval(this.refreshTimer);
			if (this._interactionEvents)
				for (let i = 0; i < this._interactionEvents.length; i++)
					document.removeEventListener(this._interactionEvents[i], this.onUserInteraction, { capture: true });
		},
		methods:
		{
			loadStatus()
			{
				this.loading = true;
				this.error = null;
				GetAwsMaintenanceStatus()
					.then(result =>
					{
						if (result.error)
						{
							this.error = result.error;
							return;
						}
						this.applyStatus(result);
					})
					.catch(ex =>
					{
						this.error = ex.message || String(ex);
					})
					.finally(() =>
					{
						this.loading = false;
					});
			},
			onUserInteraction()
			{
				this._lastInteraction = Date.now();
				if (this.autoRefreshPaused)
				{
					this.autoRefreshPaused = false;
					this.silentRefresh(); // Catch up promptly after an idle period.
				}
			},
			silentRefresh()
			{
				this.now = Date.now(); // Keep the "Status as of" readout reacting to the passage of time even when the refresh below is skipped.
				if (document.hidden)
					return;
				if (Date.now() - this._lastInteraction > autoRefreshIdleMs)
				{
					this.autoRefreshPaused = true;
					return;
				}
				if (this.loading || this.silentRefreshBusy || this.busyInstanceId || this.drainCommitBusy || !this.status)
					return;
				this.silentRefreshBusy = true;
				GetAwsMaintenanceStatus()
					.then(result =>
					{
						if (result.error)
							return;
						// Only apply the new data if something changed, so re-rendering does not disturb an in-progress slide gesture.
						if (JSON.stringify(this.statusFields(result)) !== JSON.stringify(this.statusFields(this.status)))
							this.applyStatus(result);
						else
							this.lastRefreshedAt = Date.now();
					})
					.catch(() => { })
					.finally(() => { this.silentRefreshBusy = false; });
			},
			statusFields(s)
			{
				return { displayName: s.displayName, defaultDrainSeconds: s.defaultDrainSeconds, actionDrainSeconds: s.actionDrainSeconds, targetGroups: s.targetGroups, instances: s.instances };
			},
			applyStatus(status)
			{
				let prev = this.status;
				this.status = status;
				this.lastRefreshedAt = Date.now();
				this.now = this.lastRefreshedAt;
				// Only reset the drain time inputs when the stored values changed, so refreshes do not clobber the user's uncommitted edits.
				if (!prev || prev.defaultDrainSeconds !== status.defaultDrainSeconds)
					this.defaultDrainInput = status.defaultDrainSeconds;
				if (!prev || prev.actionDrainSeconds !== status.actionDrainSeconds)
					this.actionDrainInput = status.actionDrainSeconds >= 0 ? String(status.actionDrainSeconds) : '';
				this.slideCounter++;
			},
			/// Summarizes an instance's registration state across all target groups.
			rollup(inst)
			{
				let regs = inst.registrations;
				let inService = regs.filter(r => r.state && r.state !== 'unused' && r.state !== 'draining');
				let draining = regs.filter(r => r.state === 'draining');
				if (inService.length === regs.length)
				{
					if (inService.every(r => r.state === 'healthy'))
						return { label: 'registered & healthy', badgeClass: 'badgeGood', allowDeregister: true };
					let states = [...new Set(inService.map(r => r.state))].join(', ');
					return { label: 'registered — ' + states, badgeClass: 'badgeWarn', detail: this.registrationDetail(inst), allowDeregister: true };
				}
				if (inService.length === 0)
				{
					// Registering a draining target cancels its deregistration, so the "register" action covers both cases.
					if (draining.length)
						return { label: 'draining (deregistering)', badgeClass: 'badgeDraining', detail: this.registrationDetail(inst), registerAction: 'register' };
					return { label: 'not registered', badgeClass: 'badgeOff', registerAction: 'register' };
				}
				return { label: 'partially registered', badgeClass: 'badgeWarn', detail: this.registrationDetail(inst), registerAction: 'repair', allowDeregister: true };
			},
			registrationDetail(inst)
			{
				return inst.registrations
					.map(r => r.targetGroupName + ': ' + (r.state && r.state !== 'unused' ? r.state + (r.reason ? ' (' + r.reason + ')' : '') : 'not registered'))
					.join(' · ');
			},
			/// True if deregistering this instance would leave at least one healthy target in every target group.  The server enforces this again with fresh data.
			canDeregister(inst)
			{
				return this.status.targetGroups.every(tg =>
				{
					let reg = inst.registrations.find(r => r.targetGroupArn === tg.arn);
					let selfHealthy = reg && reg.state === 'healthy' ? 1 : 0;
					return tg.healthyTargetCount - selfHealthy >= 1;
				});
			},
			registerInstance(inst)
			{
				this.busyInstanceId = inst.instanceId;
				RegisterAwsMaintenanceInstance(inst.instanceId)
					.then(result =>
					{
						if (result.error)
							toaster.Error(result.error);
						else
							toaster.Success(result.message);
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					})
					.finally(() =>
					{
						this.busyInstanceId = null;
						this.loadStatus();
					});
			},
			deregisterInstance(inst)
			{
				this.busyInstanceId = inst.instanceId;
				DeregisterAwsMaintenanceInstance(inst.instanceId)
					.then(result =>
					{
						if (result.error)
							toaster.Error(result.error);
						else
							toaster.Success(result.message);
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					})
					.finally(() =>
					{
						this.busyInstanceId = null;
						this.loadStatus();
					});
			},
			commitDefaultDrain()
			{
				if (!this.defaultDrainInputValid)
					return;
				this.drainCommitBusy = true;
				SetAwsMaintenanceDefaultDrain(this.defaultDrainInput)
					.then(result =>
					{
						if (result.error)
							toaster.Error(result.error);
						else
							toaster.Success(result.message);
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					})
					.finally(() =>
					{
						this.drainCommitBusy = false;
						this.loadStatus();
					});
			},
			saveActionDrain()
			{
				let str = String(this.actionDrainInput).trim();
				let seconds = -1;
				if (str !== '')
				{
					seconds = parseInt(str, 10);
					if (isNaN(seconds) || seconds < 0 || seconds > 3600)
					{
						toaster.Error('The drain time must be between 0 and 3600 seconds, or blank to use the default.');
						return;
					}
				}
				SetAwsMaintenanceActionDrain(seconds)
					.then(result =>
					{
						if (result.error)
							toaster.Error(result.error);
						else
						{
							toaster.Success(result.message);
							if (this.status)
								this.status.actionDrainSeconds = seconds;
						}
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					});
			}
		}
	}
</script>

<style scoped>
	/* Match the page background and padding used by other client pages (e.g. the Release tab's .pageRoot). */
	.awsMaintenanceRoot
	{
		padding: 8px 8px 1em 8px;
		background-color: #EFEDE6;
		overflow-y: auto;
	}

		.awsMaintenanceRoot > *
		{
			max-width: 950px;
		}

	.pageHeading
	{
		font-size: 32px;
		margin-bottom: 0.33em;
	}

	.secondaryHeading
	{
		font-size: 24px;
		margin-bottom: 0.33em;
	}

	.card
	{
		border: 1px solid #DDDDDD;
		padding: 12px;
		background-color: #FFFFFF;
		margin-bottom: 1.5em;
		box-shadow: 2px 2px 2px rgba(0,0,0,0.4);
	}

	.errorBox
	{
		background-color: #FFE0E0;
		border: 1px solid #C00000;
		color: #660000;
		padding: 8px 12px;
		margin-bottom: 1em;
		white-space: pre-wrap;
		word-break: break-word;
	}

	.warningBox
	{
		background-color: #FFEFC9;
		border: 1px solid #E0B93F;
		color: #7A5200;
		padding: 8px 12px;
		margin: 8px 0;
	}

	.toolbarCard
	{
		display: flex;
		flex-wrap: wrap;
		gap: 8px 14px;
		align-items: center;
	}

	.loadingRow,
	.loadingInline
	{
		display: inline-flex;
		align-items: center;
		gap: 10px;
	}

	.loadingRow
	{
		display: flex;
		padding: 8px;
	}

	.hintText
	{
		color: #555555;
		font-size: 0.92em;
		margin: 0 0 10px 0;
	}

	.mutedText
	{
		color: #888888;
	}

	.readOnlyBadge
	{
		font-size: 0.85em;
		padding: 1px 8px;
		border-radius: 9px;
		background-color: #EEEEEE;
		color: #666666;
		cursor: help;
	}

	.lbHeading
	{
		font-size: 26px;
		margin-bottom: 0.5em;
	}

	code.inline
	{
		font-family: Consolas, monospace;
		background-color: #F4F4F4;
		border-radius: 3px;
		padding: 0px 5px;
	}

	.instanceRow
	{
		padding: 12px 6px;
		border-bottom: 1px solid #E4E4E4;
	}

		.instanceRow:last-child
		{
			border-bottom: none;
		}

	.instanceInfo
	{
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
	}

	.instanceName
	{
		font-weight: bold;
		font-size: 1.15em;
	}

	.instanceDetail
	{
		color: #777777;
		font-size: 0.88em;
		margin: 4px 0 0 18px;
	}

	.instanceActions
	{
		display: flex;
		flex-wrap: wrap;
		gap: 10px 16px;
		margin: 10px 0 0 18px;
	}

	.statusDot
	{
		flex: 0 0 auto;
		display: inline-block;
		width: 10px;
		height: 10px;
		border-radius: 50%;
		cursor: help;
	}

	.dotGreen
	{
		background-color: #2E9E2E;
	}

	.dotGray
	{
		background-color: #AAAAAA;
	}

	.stateBadge
	{
		display: inline-block;
		padding: 2px 10px;
		border-radius: 10px;
		font-weight: bold;
		font-size: 0.9em;
		white-space: nowrap;
		cursor: help;
	}

	.badgeGood
	{
		background-color: #D4F5D4;
		color: #0A5A0A;
		border: 1px solid #77CC77;
	}

	.badgeWarn
	{
		background-color: #FFEFC9;
		color: #7A5200;
		border: 1px solid #E0B93F;
	}

	.badgeDraining
	{
		background-color: #FFE3C9;
		color: #8A4500;
		border: 1px solid #E09A3F;
	}

	.badgeOff
	{
		background-color: #EEEEEE;
		color: #666666;
		border: 1px solid #CCCCCC;
	}

	/* Red visual cue for the deregister slider. */
	.slideRed :deep(.slideunlock)
	{
		--su-color-bg: #F5DCDC;
		--su-color-progress-normal-bg: #E5AFAF;
		--su-color-progress-complete-bg: #CC3333;
	}

	.formRow
	{
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
		margin: 8px 0;
	}

	.formLabel
	{
		font-weight: bold;
	}

	.drainInput
	{
		width: 90px;
		padding: 5px 8px;
		border: 1px solid #AAAAAA;
		border-radius: 4px;
		font-size: 15px;
	}

	.drainReadout
	{
		display: flex;
		flex-wrap: wrap;
		gap: 6px 14px;
		margin: 4px 0 8px 0;
		color: #555555;
		font-size: 0.9em;
	}

	.drainTgValue
	{
		cursor: help;
	}

		.drainTgValue.drainWrong
		{
			color: #7A5200;
			font-weight: bold;
		}

	.drainCommitRow
	{
		margin: 10px 0;
	}

	.actionDrainSection
	{
		margin-top: 16px;
		border-top: 1px solid #E0E0E0;
		padding-top: 10px;
	}
</style>
